import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { isAdminUser, getOrderEmailSettings, setOrderEmailSettings } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PackageCheck, Truck, Users, MessageSquare, Star, XCircle } from "lucide-react";

const ROWS = [
  { key: "enabled", icon: PackageCheck, title: "Order Confirmation on Processing",
    desc: "Master switch. When an order turns Processing the buyer gets the “Order Confirmation” email (Marketing AI tab). Turn this on after retiring the Airtable trigger." },
  { key: "ambassador_copy", icon: Users, title: "Ambassador heads-up",
    desc: "Also send the customer’s ambassador the short “Ambassador order alert” when the order is placed, and “Ambassador shipped alert” (with tracking) when it ships." },
  { key: "shipped", icon: Truck, title: "Shipped notification",
    desc: "When the order is Completed and a UPS tracking number arrives, send “Order Shipped” with the tracking link." },
  { key: "cancelled", icon: XCircle, title: "Cancelled notification",
    desc: "When an order turns Cancelled, send the buyer the “Order Cancelled” email (Marketing AI tab) with the order summary and a link back to their dealer's shop." },
  { key: "review", icon: Star, title: "Review request (10 days after shipping)",
    desc: "Email “How did it go?” with a Write-a-review button per product (opens the product page on the customer’s dealer storefront). Reviewers earn rewards automatically." },
  { key: "sms_processing", icon: MessageSquare, title: "Text on new order (Twilio)",
    desc: "Text the buyer “we received your order #… ($…)” when it turns Processing. Uses our Twilio marketing number." },
  { key: "sms_shipped", icon: MessageSquare, title: "Text UPS tracking (Twilio)",
    desc: "Text the buyer the UPS tracking number + link when the order is Completed with tracking." },
];

function Toggle({ on, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-zinc-300"} disabled:opacity-40`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

// Admin: automatic order emails (processing → confirmation + ambassador copy, tracking → shipped)
export default function OrderEmailAutomation({ userRecordId }) {
  const { user } = useAuth();
  const admin = isAdminUser(user);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    if (!admin || !userRecordId) return;
    getOrderEmailSettings(userRecordId).then(setS).catch(() => setS({}));
  }, [admin, userRecordId]);

  if (!admin) return null;

  async function flip(key, val) {
    setSaving(key);
    try {
      const res = await setOrderEmailSettings(userRecordId, { [key]: val });
      setS((prev) => ({ ...prev, ...res }));
      toast.success(val ? "Turned on" : "Turned off");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving("");
    }
  }

  const missing = s?.templates ? Object.entries(s.templates).filter(([, ok]) => !ok).map(([k]) => k) : [];

  return (
    <div className="mt-5 pt-5 border-t border-zinc-100" data-testid="order-email-automation">
      <div className="text-sm font-semibold text-zinc-900">Automatic order emails</div>
      <p className="text-[11px] text-zinc-500 mt-0.5">
        Sent from the house mailbox, personalized with the customer’s ambassador. Templates live in the Marketing AI tab
        (Order Confirmation · Order Shipped · Ambassador order alert). Checks Airtable every minute.
      </p>
      {!s ? (
        <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
      ) : (
        <div className="mt-3 space-y-2">
          {s.send_enabled === false && (
            <p className="text-[11px] rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5" data-testid="order-email-send-disabled">
              Live sending is turned off in this environment (ORDER_AUTOMATION_SEND=false). Orders are tracked but no customer emails/texts go out from here.
            </p>
          )}
          <p className="text-[11px] text-zinc-400" data-testid="order-email-burst-note">
            Safety: if one check ever finds more than {s.burst_limit ?? 10} unsent notifications of a kind, they’re marked handled instead of sent.
          </p>
          {ROWS.map((r) => {
            const Icon = r.icon;
            const disabled = r.key !== "enabled" && !s.enabled;
            return (
              <div key={r.key} className={`flex items-start gap-3 rounded-xl border p-3 ${disabled ? "border-zinc-100 opacity-60" : "border-zinc-200"}`}>
                <Icon size={16} className="mt-0.5 text-violet-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800">{r.title}</div>
                  <div className="text-[11px] text-zinc-500 leading-snug">{r.desc}</div>
                </div>
                {saving === r.key ? (
                  <Loader2 size={16} className="animate-spin text-zinc-400 mt-1" />
                ) : (
                  <Toggle on={!!s[r.key]} disabled={disabled} onChange={(v) => flip(r.key, v)} testId={`order-email-toggle-${r.key}`} />
                )}
              </div>
            );
          })}
          {missing.length > 0 && (
            <p className="text-[11px] text-amber-700" data-testid="order-email-missing">
              Missing template(s) in Airtable email-template: {missing.join(", ")}
            </p>
          )}
          {s.recent?.length > 0 && (
            <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-2" data-testid="order-email-recent">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Recent automatic sends</div>
              {s.recent.slice(0, 6).map((r, i) => (
                <div key={i} className="flex justify-between text-[11px] text-zinc-600 py-0.5">
                  <span>#{r.order_id} · {r.kind}</span>
                  <span className={r.status === "sent" ? "text-emerald-600" : "text-rose-600"} title={r.error}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
